import { css } from '@emotion/css';
import { Badge, Group, type MantineTheme, Stack, Text, useMantineTheme } from '@mantine/core';
import type { Id } from 'api/models';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import HTML from 'shared/components/HTML/HTML';
import type { RecentActivities as RecentActivitiesType } from 'shared/hooks/use-load-dashboard';
import { ActivityItem } from '../ActivityItem';
import { ACTIVITIES, activityHtml } from './activities';

const boldStyles = (theme: MantineTheme) => css`
  color: ${theme.colors.gray[7]};

  b {
    font-weight: 500;
    color: ${theme.colors.gray[10]};
  }
`;

type Props = {
  data: RecentActivitiesType;
  accountManagerId?: Id;
};

export default function RecentActivities({ data, accountManagerId }: Props) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const company = useCompanyConfiguration();
  const profile = useProfile();

  const hasInvoicesToTreat = data.invoicesToTreat > 0;
  const hasClientsToTreat = data.debtorsToHandle > 0;
  const shouldDisplayUnprocessedTransactions =
    company.codabox_integration.active || company.ponto_integration.active;

  return (
    <Panel style={{ height: '100%' }} title={t(i18nKeys.YOUR_ACCOUNT)}>
      <Stack>
        {hasClientsToTreat && (
          <ActivityItem
            url={ACTIVITIES.debtorsToHandle.path}
            Icon={ACTIVITIES.debtorsToHandle.icon}
            highlight
          >
            <Group gap={8}>
              <HTML
                html={activityHtml('debtorsToHandle', data.debtorsToHandle, t)}
                className={boldStyles(theme)}
              />
              <Badge
                styles={{ root: { border: `1px solid ${theme.colors.coral[4]}` } }}
                color="coral.2"
                size="sm"
                radius={6}
              >
                <Text fw={500} fz={12} c="coral.6">
                  Beta
                </Text>
              </Badge>
            </Group>
          </ActivityItem>
        )}
        {hasInvoicesToTreat && (
          <ActivityItem
            url={`${ACTIVITIES.invoicesToTreat.path}${accountManagerId ? `?user_id=${accountManagerId}` : ''}`}
            Icon={ACTIVITIES.invoicesToTreat.icon}
            highlight
          >
            <HTML
              html={activityHtml('invoicesToTreat', data.invoicesToTreat, t)}
              className={boldStyles(theme)}
            />
          </ActivityItem>
        )}

        {company.package.can_use_tasks && (
          <ActivityItem
            url={`${ACTIVITIES.ongoingTasksCount.path}&user_id=${accountManagerId ?? profile.id}`}
            Icon={ACTIVITIES.ongoingTasksCount.icon}
          >
            <HTML
              html={activityHtml('ongoingTasksCount', data.ongoingTasksCount, t)}
              className={boldStyles(theme)}
            />
          </ActivityItem>
        )}

        {shouldDisplayUnprocessedTransactions && (
          <ActivityItem url={ACTIVITIES.paymentCount.path} Icon={ACTIVITIES.paymentCount.icon}>
            <HTML
              html={activityHtml('paymentCount', data.paymentCount, t)}
              className={boldStyles(theme)}
            />
          </ActivityItem>
        )}

        {company.package.can_use_external_mails && (
          <ActivityItem
            url={`${ACTIVITIES.unprocessedEmailsCount.path}${
              accountManagerId != null ? `&account_manager_id=${accountManagerId}` : ''
            }`}
            Icon={ACTIVITIES.unprocessedEmailsCount.icon}
          >
            <HTML
              html={activityHtml('unprocessedEmailsCount', data.unprocessedEmailsCount, t)}
              className={boldStyles(theme)}
            />
          </ActivityItem>
        )}

        {data.unreadThirdPartyMessagesCount > 0 && (
          <ActivityItem
            url={ACTIVITIES.unreadThirdPartyMessagesCount.path}
            Icon={ACTIVITIES.unreadThirdPartyMessagesCount.icon}
          >
            <HTML
              html={activityHtml(
                'unreadThirdPartyMessagesCount',
                data.unreadThirdPartyMessagesCount,
                t,
              )}
              className={boldStyles(theme)}
            />
          </ActivityItem>
        )}

        {data.failedRemindersCount > 0 && (
          <ActivityItem
            url={ACTIVITIES.failedRemindersCount.path}
            Icon={ACTIVITIES.failedRemindersCount.icon}
          >
            <HTML
              html={activityHtml('failedRemindersCount', data.failedRemindersCount, t)}
              className={boldStyles(theme)}
            />
          </ActivityItem>
        )}

        {data.latePaymentPlansCount > 0 && (
          <ActivityItem
            url={ACTIVITIES.latePaymentPlansCount.path}
            Icon={ACTIVITIES.latePaymentPlansCount.icon}
          >
            <HTML
              html={activityHtml('latePaymentPlansCount', data.latePaymentPlansCount, t)}
              className={boldStyles(theme)}
            />
          </ActivityItem>
        )}
      </Stack>
    </Panel>
  );
}
